import { DateTime } from 'luxon';
import { NO_VALUE } from '../../../Ticker';
import { MARKET_DATA_SOURCE_ZONE } from './formats';

export type DateFilterCellValue = Date | string | null | undefined;

/**
 * Resolves a filter cell value to the moment it represents in the zone it is
 * displayed in. Display strings are parsed as UTC wall clock, so their calendar
 * fields already describe the shown day; instants coming from filterValueGetter
 * have to be read back in the display zone to recover that day.
 */
export const toFilterCellDateTime = (
  cellValue: DateFilterCellValue,
  parseDisplayValue: (value: unknown) => DateTime | null,
  timezone?: string,
): DateTime | null => {
  if (cellValue == null || cellValue === '') return null;

  const displayZone = timezone ?? MARKET_DATA_SOURCE_ZONE;

  if (cellValue instanceof Date) {
    if (isNaN(cellValue.getTime())) return null;
    return DateTime.fromJSDate(cellValue, { zone: displayZone });
  }

  if (typeof cellValue === 'string') {
    if (cellValue === NO_VALUE) return null;

    const parsed = parseDisplayValue(cellValue);
    if (parsed?.isValid) return parsed;

    const fallback = new Date(cellValue);
    return isNaN(fallback.getTime()) ? null : DateTime.fromJSDate(fallback, { zone: displayZone });
  }

  return null;
};

const toDayOrdinal = (year: number, month: number, day: number): number => year * 10000 + month * 100 + day;

/**
 * ag-grid hands us the picked day as browser-local midnight, so both sides are
 * compared on calendar fields alone. Normalizing the cell with Date.setHours
 * instead would shift it by the browser's UTC offset and drop rows from the very
 * day the user picked.
 */
export const compareDateFilterCell = (
  filterLocalDateAtMidnight: Date,
  cellValue: DateFilterCellValue,
  parseDisplayValue: (value: unknown) => DateTime | null,
  timezone?: string,
): number => {
  const cellDateTime = toFilterCellDateTime(cellValue, parseDisplayValue, timezone);
  if (!cellDateTime?.isValid) return -1;

  const filterDay = toDayOrdinal(
    filterLocalDateAtMidnight.getFullYear(),
    filterLocalDateAtMidnight.getMonth() + 1,
    filterLocalDateAtMidnight.getDate(),
  );
  const diff = toDayOrdinal(cellDateTime.year, cellDateTime.month, cellDateTime.day) - filterDay;

  return diff < 0 ? -1 : diff > 0 ? 1 : 0;
};
